/**
 * Protected Route Component
 * Module 07 - Dashboard UI
 * Section 03 - Step 3.5
 * 
 * Wrapper component that protects routes requiring authentication
 * Redirects unauthorised users to the login page
 */

import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import LoadingSpinner from '../common/LoadingSpinner';

/**
 * Protected Route Component
 * 
 * Checks if user is authenticated before rendering child routes
 * Shows loading spinner while checking authentication status
 * Redirects to login if user is not authenticated
 */
const ProtectedRoute: React.FC = () => {
  const { isAuthenticated, isLoading } = useAuth();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  // Redirect to login if not authenticated
  // The `replace` prop replaces the current entry in the history stack
  // This prevents users from going back to the protected route after logging out
  return isAuthenticated ? <Outlet /> : <Navigate to="/login" replace />;
};

export default ProtectedRoute;
